import cx from 'classnames';
import uniqueId from 'lodash/uniqueId';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal, { OnAfterOpenCallback } from 'react-modal';
import { LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { IntercomWrapper } from 'src/services/intercom';

import { Button } from '../Button/Button';
import { IconType } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Modal, ModalWrapperProps as ModalLegacyProps } from '../Modal/Modal';
import { TextV2 } from '../TextV2/TextV2';

import styles from './StructuredModal.scss';

type StructuredModalVariant = 'standard' | 'slim';

type CoreStructuredModalProps = Omit<ModalLegacyProps, 'isOpen'> & {
  title?: string;
  description?: string;
  variant?: StructuredModalVariant;
  actions?: StructuredModalAction[];
};

type StructuredModalAction = {
  label: string;
  variant: LinkVariant;
} & ({
  action: () => boolean | void;
} | {
  isCloseButton: boolean;
});

interface StructuredModalPropsWithState extends CoreStructuredModalProps {
  /**
   * @example
   * const [isOpen, setIsOpen] = useState(false);
   * <StructuredModal
   *   modalState={[ isOpen, setIsOpen ]}
   *   // ...other props
   * />
   */
  modalState: StructuredModalState;
}

interface StructuredModalPropsWithOpen extends CoreStructuredModalProps {
  isOpen: boolean;
}

export type StructuredModalProps = StructuredModalPropsWithState | StructuredModalPropsWithOpen;

export type StructuredModalState = [boolean, (state: boolean) => any];
type StructuredModalStateWithOptionalState = [boolean, (((state: boolean) => any) | undefined)];

/**
 * Displays a modern modal
 */
export const StructuredModal = React.forwardRef<ReactModal, StructuredModalProps>(({
  title,
  description,
  actions,
  children,
  variant = 'standard',
  closeTimeoutMS = 500,
  aria,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const uid = useRef(uniqueId());
  const modalState: StructuredModalStateWithOptionalState = [
    'isOpen' in props ? props.isOpen : props.modalState[0],
    'isOpen' in props ? undefined : props.modalState[1],
  ];
  const modalActive = modalState[0];
  const setModalActive = modalState[1];

  if (!aria) {
    aria = {
      modal: true,
      labelledby: title ? `structured-modal-title-${uid.current}` : undefined,
    };
  }

  const handleOpen: OnAfterOpenCallback = obj => {
    if (props.onAfterOpen) props.onAfterOpen(obj);
    IntercomWrapper.hideLauncher();
  };

  const handleClose = () => {
    if (setModalActive) setModalActive(false);
    IntercomWrapper.showLauncher();
  };

  const handleAction = (modalAction: StructuredModalAction) => {
    if ('isCloseButton' in modalAction && modalAction.isCloseButton) {
      handleClose();
    } else if ('action' in modalAction && modalAction.action) {
      const result = modalAction.action();
      if (!result) {
        handleClose();
      }
    }
  };

  const modalContentClasses: ReactModal.Classes = {
    base: cx(styles.modal, {
      [styles.modalSlim]: variant === 'slim',
    }),
    afterOpen: styles.modalLoaded,
    beforeClose: styles.modalClosing,
  };

  const modalOverlayClasses: ReactModal.Classes = {
    base: styles.overlay,
    afterOpen: styles.overlayLoaded,
    beforeClose: styles.overlayClosing,
  };

  return (
    <Modal
      ref={ref}
      closeTimeoutMS={closeTimeoutMS}
      isOpen={modalActive}
      className={modalContentClasses}
      overlayClassName={modalOverlayClasses}
      onAfterOpen={handleOpen}
      onRequestClose={handleClose}
      focusElementId={`structured-modal-close-${uid.current}`}
      aria={aria}
      {...props}
    >
      <IconButton
        id={`structured-modal-close-${uid.current}`}
        onClick={handleClose}
        className={styles.closeIcon}
        aria-label={t('close')}
        type={IconType.CLOSE}
      />
      <div className={styles.modalContent}>
        {title && <TextV2 tagName="h2" value={title} id={`structured-modal-title-${uid.current}`} />}
        {description && (
          <TextV2 tagName="div" value={description} isMarkdown />
        )}
        {children}
      </div>
      {actions && (
        <div className={styles.modalActions}>
          {actions.map(action => (
            <Button key={action.label} variant={action.variant} onClick={() => handleAction(action)}>{action.label}</Button>
          ))}
        </div>
      )}
    </Modal>
  );
});
